import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  AttributionControl,
} from "react-leaflet";
import L from "leaflet";
import { useTranslation } from "react-i18next";
import { localizedField } from "@/lib/localized";


const icon = L.divIcon({
  className: "",
  html: `<div style="background:#0B2545;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #C5A059;box-shadow:0 2px 8px rgba(0,0,0,.3)"></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const highlightIcon = L.divIcon({
  className: "",
  html: `<div style="background:#C5A059;width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.45)"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 20],
  popupAnchor: [0, -18],
});

function FitBounds({ points, highlight }) {
  const map = useMap();

  useEffect(() => {
    if (highlight?.lat && highlight?.lng) {
      map.setView([highlight.lat, highlight.lng], 15, { animate: true });
      return;
    }

    if (!points.length) return;

    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15);
    } else {
      map.fitBounds(
        points.map((point) => [point.lat, point.lng]),
        { padding: [40, 40], maxZoom: 14 }
      );
    }
  }, [points, highlight, map]);

  return null;
}

export default function PropertyMap({
  properties = [],
  height = "500px",
  onSelect,
  highlight = null,
}) {
  const { t, i18n } = useTranslation();
  const [mapStyle, setMapStyle] = useState("map");
  const wrapperRef = useRef(null);

  const points = properties.filter(
    (property) => Number(property.lat) && Number(property.lng)
  );

  const center = points.length
    ? [Number(points[0].lat), Number(points[0].lng)]
    : [40.2, -8.4];

  const primaryPoint = highlight?.lat
    ? highlight
    : points.length
      ? points[0]
      : null;

  const openGoogleMaps = () => {
    if (!primaryPoint) return;

    window.open(
      `https://www.google.com/maps/search/?api=1&query=${primaryPoint.lat},${primaryPoint.lng}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const toggleFullscreen = async () => {
    if (!wrapperRef.current) return;

    if (!document.fullscreenElement) {
      await wrapperRef.current.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  };

  return (
    <div
      ref={wrapperRef}
      style={{ height }}
      className="relative rounded-sm overflow-hidden border border-slate-200 bg-white"
      data-testid="property-map"
    >
      <MapContainer
        center={center}
        zoom={7}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
        attributionControl={false}
      >
        <AttributionControl prefix={false} />

        {mapStyle === "map" ? (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        ) : (
          <TileLayer
            attribution="Tiles &copy; Esri"
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        )}

        <FitBounds points={points} highlight={highlight} />

        {points.map((property) => (
          <Marker
            key={property.id}
            position={[Number(property.lat), Number(property.lng)]}
            icon={icon}
          >
            <Popup>
              <div className="font-sans">
                <div className="font-semibold text-slate-900">
                  {localizedField(property, "title", i18n)}
                </div>

                <div className="text-xs text-slate-500">
                  {t("cityDistrict", "{{city}}, {{district}}", {
                    city: property.city,
                    district: property.district,
                  })}
                </div>

                {onSelect && (
                  <button
                    type="button"
                    onClick={() => onSelect(property)}
                    className="text-[#C5A059] text-xs mt-1 underline"
                  >
                    {t("viewDetails", "View details")}
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {highlight?.lat && highlight?.lng && (
          <Marker
            position={[Number(highlight.lat), Number(highlight.lng)]}
            icon={highlightIcon}
          >
            <Popup>
              <div
                className="text-sm font-medium text-slate-900"
                data-testid="property-map-highlight"
              >
                {highlight.name}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      <div className="absolute left-3 top-3 z-[500] flex overflow-hidden rounded-sm border border-slate-300 bg-white shadow-md">
        <button
          type="button"
          onClick={() => setMapStyle("map")}
          className={`px-4 py-2 text-sm font-medium ${
            mapStyle === "map"
              ? "bg-[#0B2545] text-white"
              : "bg-white text-slate-700"
          }`}
        >
          {t("map", "Map")}
        </button>

        <button
          type="button"
          onClick={() => setMapStyle("satellite")}
          className={`px-4 py-2 text-sm font-medium ${
            mapStyle === "satellite"
              ? "bg-[#0B2545] text-white"
              : "bg-white text-slate-700"
          }`}
        >
          {t("satellite", "Satellite")}
        </button>
      </div>

      <button
        type="button"
        onClick={toggleFullscreen}
        title={t("fullScreen", "Full screen")}
        className="absolute right-3 top-3 z-[500] h-11 w-11 border border-slate-300 bg-white text-xl text-[#0B2545] shadow-md hover:bg-slate-50"
      >
        ⛶
      </button>

      {primaryPoint && (
        <button
          type="button"
          onClick={openGoogleMaps}
          className="absolute bottom-6 left-1/2 z-[500] -translate-x-1/2 border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-[#0B63CE] shadow-md hover:bg-slate-50"
        >
          {t("openInGoogleMaps", "Open in Google Maps")}
        </button>
      )}
    </div>
  );
}
